import React from "react";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import {
    AlphanumericWithSpecialCharacters,
    Email,
} from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { PhysicalAddressType } from "../../../../../../../Wrap/Constants/Enums/Customer/PhysicalAddressType";
import { Company } from "../../../../../../../Wrap/Models/Models/Customer/Company";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { AddAddress } from "../../../NewBusinessWizard/Components/FullClientDetail/AddAddress";
import { CorporateTrusteeCompanyTypeOption, CompanyTypeMap } from "../../Resource/Contents";
import { setValueByKey } from "../../../../../../Shared/Table/Utils/TableUtils";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { ValidatorContents } from "../../../../../Common/ValidatorHelper";
import { EmailAddressType } from "../../../../../../../Wrap/Constants/Enums/EmailAddressType";
import { PhoneNumberDetails } from "../../../Common/ClientDetails/PhoneNumberDetails";
import { CompanyType } from "../../../../../../../Wrap/Constants/Enums/Customer/CompanyType";
import { FullClientDetailHelper } from "../../../Common/FullClientDetail/FullClientDetailHelper";
import { ClientRecordAction } from "../../../ClientRecord/Action/ClientRecordAction";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { FullClientDetailModel } from "../../../../../../../Wrap/Models/Models/Customer/FullClientDetailModel";
import { CustomerTaxResidencyModel } from "../../../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { AccountRoleActorCategory } from "../../../../../../../Wrap/Constants/Enums/AccountRoleActorCategory";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { TaxDetailFatca } from "../../../NewBusinessWizard/Components/FullClientDetail/TaxDetailFatca";
import { CustomerContextStoreStatic } from "../../../NewBusinessWizard/Stores/Variable";

interface IAddCorporateTrusteeProps extends IProcessComponentProps {
    data: Company;
    existingCustomer: boolean;
    customerId: number;
    ClientModel: FullClientDetailModel;
    OwnerAdviserId: number;
    onSetClientModel: (ClientModel) => void;
}

interface IAddCorporateTrusteeState {
    useAddressForCorrespondence: boolean;
    isLoading: boolean;
    taxResidencyOtherThanUK: boolean;
    disableTax: boolean;
    existingClientInfo: FullClientDetailModel;
    updateClient: boolean;
}

export class AddCorporateTrustee extends ProcessComponent<IAddCorporateTrusteeProps, IAddCorporateTrusteeState> {
    constructor(props: any) {
        super(props);
        this.state = {
            useAddressForCorrespondence: null,
            isLoading: false,
            taxResidencyOtherThanUK: false,
            disableTax: false,
            existingClientInfo: null,
            updateClient: true,
        };
        this.subscribeToStore(CustomerContextStoreStatic, () => this.onCustomerContextStoreChange());
    }

    onCustomerContextStoreChange(): void {
        this.loadCustomerFromStore();

        let initArray = [];
        initArray = FullClientDetailHelper.initTaxModel(initArray, this.props.data);
        initArray = FullClientDetailHelper.updatePostalAddress(initArray, this.props.data);
        this.onFullClientChange(initArray);
    }

    loadCustomerFromStore(): void {
        const val = CustomerContextStoreStatic.getData();
        if (this.props.data == null) {
            this.props.onChange(val);
        }
        this.setState({
            isLoading: false,
            existingClientInfo: val,
        });
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, customerId, existingCustomer } = this.props;
        if (customerId && existingCustomer && !data?.CompanyName) {
            this.getCompanyDetail();
        } else {
            this.initialiseModel(this.props.data);
        }
        if (CommonHelper.isNullOrEmpty(this.props.ClientModel)) {
            this.populateFullClientDetailsFromProspect();
        } else {
            this.setState({ updateClient: false });
        }
    }

    componentDidUpdate(prevProps: any): void {
        super.componentDidUpdate(prevProps);
        if (prevProps.customerId != this.props.customerId && this.props.customerId != null) {
            this.getCompanyDetail();
        }
    }

    private getCompanyDetail() {
        const { data, customerId } = this.props;
        this.setState({ isLoading: true });
        ClientRecordAction.getCompanyDetail(customerId.toString(), (result: Company) => {
            this.props.onChange(result);
            this.initialiseModel(result);
            this.setState({ isLoading: false });
        });
    }

    private initialiseModel(data: Company) {
        if (!data?.CompanyType) {
            this.props.onChange({ CompanyType: CompanyType.LtdCo });
        }
        if (data?.AddressDetails && data?.AddressDetails.length == 2) {
            if (
                FullClientDetailHelper.isSameAddress(data?.AddressDetails[0].Address, data?.AddressDetails[1].Address)
            ) {
                this.setState({ useAddressForCorrespondence: true });
            } else {
                this.setState({ useAddressForCorrespondence: false });
            }
        }
    }

    private onFullClientChange = (changes: { value: any; path: string }[]) => {
        const data = this.props.data;
        let _model = { ...data };
        changes.forEach((change) => {
            _model = setValueByKey(_model, change.path, change.value);
        });
        this.props.onChange(_model);
    };

    private onClientChange = (changes: { value: any; path: string }[]) => {
        const { ClientModel } = this.props;

        this.props.onSetClientModel(this.setValue(ClientModel, changes));
        //console.log(_model);
    };

    isValid() {
        const flag = super.isValid();
        if (!this.props.data?.AddressDetails) {
            this.props.showErrorMsgPopUp(["Please fill in registered and correspondence address"], true);
            return false;
        }
        if (
            this.props.data?.AddressDetails.filter((x) => x.PhysicalAddressType == PhysicalAddressType.Registered)
                .length == 0
        ) {
            this.props.showErrorMsgPopUp(["Please fill in registered address"], true);
            return false;
        }
        if (
            this.props.data?.AddressDetails.filter((x) => x.PhysicalAddressType == PhysicalAddressType.Postal).length ==
            0
        ) {
            this.props.showErrorMsgPopUp(["Please fill in correspondence address"], true);
            return false;
        }
        return flag;
    }

    private hasRegisteredAddress(): boolean {
        return this.props.data?.AddressDetails?.some(
            (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Registered
        );
    }

    private setValue = (data, changes: { value: any; path: string }[]) => {
        let _model = { ...data };
        changes.forEach((change) => {
            _model = setValueByKey(_model, change.path, change.value);
        });
        return _model;
    };

    private populateFullClientDetailsFromProspect() {
        const { OwnerAdviserId } = this.props;

        let model: FullClientDetailModel = {
            ActorOwnerId: OwnerAdviserId,
            ActorId: null,
            ActorCategory: AccountRoleActorCategory.Customer,
            PersonalDetails: {},
            AddressDetails: [],
        };

        let initArray = [];
        initArray = FullClientDetailHelper.initTaxModel(initArray, model);
        initArray = FullClientDetailHelper.updatePostalAddress(initArray, model);
        model = this.setValue(model, initArray);
        this.props.onSetClientModel(model);

        this.setState({ updateClient: false }); //this.setState({ residencies: model.CustomerTaxResidencyDetails });
    }

    public UpdateResidencies() {
        // const { residencies } = this.state;
        if (!CommonHelper.isNullOrEmpty(this.props?.ClientModel?.CustomerTaxResidencyDetails?.some)) {
            const detail = this.props.ClientModel.CustomerTaxResidencyDetails.filter((entry) => {
                return entry.IsTaxResident;
            }).map(
                (entry): CustomerTaxResidencyModel => {
                    return {
                        Country: entry.Country,
                        IsTaxResident: entry.IsTaxResident,
                        PrimaryTaxResidency: entry.PrimaryTaxResidency,
                        TaxReferenceType: entry.TaxReferenceType,
                        TaxReference: entry.TaxReference,
                        TaxResidencyForPensionRelief: entry.TaxResidencyForPensionRelief,
                        TaxExempt: entry.TaxExempt,
                        TaxExemptionReason: entry.TaxExemptionReason,
                    };
                }
            );
            //this.setState({ residencies: detail });
        }
    }
    public render(): JSX.Element {
        const { data, existingCustomer } = this.props;
        const { updateClient } = this.state;
        if (this.state.isLoading) {
            return <PartLoading isLoading={true} />;
        }

        return (
            <>
                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.CompanyName}
                    label={"Company Name"}
                    regex={{
                        errorMessage: "Only alphanumeric and special characters allowed.",
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    maxLength={35}
                    onChange={(value) => this.props.onChange({ CompanyName: value })}
                    required={"Please enter name"}
                />

                <SelectEditor
                    options={CorporateTrusteeCompanyTypeOption}
                    getText={(v) => {
                        return CompanyTypeMap[v];
                    }}
                    cssWrapper="control-size-430"
                    required="Please select"
                    label="Company type"
                    selected={data?.CompanyType}
                    validationRules={[
                        {
                            errorMessage: "Please select",
                            validator: (v) => {
                                return v != CompanyType.NotSpecified;
                            },
                        },
                    ]}
                    onChange={(value) => this.props.onChange({ CompanyType: value })}
                />

                <AddAddress
                    label="Registered address"
                    onAddressChange={(v) => this.props.onChange({ Address: v })}
                    address={data?.AddressDetails?.find(
                        (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Registered
                    )}
                    index={
                        data?.AddressDetails?.length == 0 || !data?.AddressDetails
                            ? 0
                            : !this.hasRegisteredAddress()
                            ? 1
                            : data?.AddressDetails?.findIndex(
                                  (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Registered
                              )
                    }
                    canChooseCountry={false}
                    onCCMChange={(changes) => {
                        this.onFullClientChange(changes);
                    }}
                    addressType={PhysicalAddressType.Registered}
                    addressList={data?.AddressDetails}
                    resetUseAddressForCorrespondence={() => {}}
                    onRef={(ref) => this.appendChild(ref)}
                    onChange={() => {}}
                    data={null}
                    disabled={false}
                />

                {data?.AddressDetails && this.hasRegisteredAddress() && (
                    <>
                        <RadiosEditor
                            cssWrapper="rg-inline mb-16"
                            selected={this.state.useAddressForCorrespondence}
                            label={"Use this address for all correspondence"}
                            options={[true, false]}
                            getText={(v) => (v ? "Yes" : "No")}
                            disabled={false}
                            required={"Please select"}
                            onChange={(v) => {
                                const data = this.props.data?.AddressDetails;
                                const index = data?.findIndex(
                                    (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Registered
                                );

                                const postalindex = index == 1 ? 0 : 1;
                                if (v) {
                                    this.onFullClientChange([
                                        {
                                            value: PhysicalAddressType.Postal,
                                            path: `AddressDetails.${postalindex}.PhysicalAddressType`,
                                        },
                                        {
                                            value: data[index].Address.Address1,
                                            path: `AddressDetails.${postalindex}.Address.Address1`,
                                        },
                                        {
                                            value: data[index].Address.Address2,
                                            path: `AddressDetails.${postalindex}.Address.Address2`,
                                        },
                                        {
                                            value: data[index].Address.Address3,
                                            path: `AddressDetails.${postalindex}.Address.Address3`,
                                        },
                                        {
                                            value: data[index].Address.Address4,
                                            path: `AddressDetails.${postalindex}.Address.Address4`,
                                        },
                                        {
                                            value: data[index].Address.Country,
                                            path: `AddressDetails.${postalindex}.Address.Country`,
                                        },
                                        {
                                            value: data[index].Address.PostCode,
                                            path: `AddressDetails.${postalindex}.Address.PostCode`,
                                        },
                                    ]);
                                } else {
                                    data.splice(postalindex, 1);
                                    this.onFullClientChange([{ value: data, path: `AddressDetails` }]);
                                }
                                this.setState({ useAddressForCorrespondence: v });
                            }}
                        />
                        {this.state.useAddressForCorrespondence == false && (
                            <AddAddress
                                label="Correspondence address"
                                onAddressChange={(v) => this.props.onChange({ CorrespondenceAddress: v })}
                                address={data?.AddressDetails?.find(
                                    (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Postal
                                )}
                                index={
                                    data?.AddressDetails.findIndex(
                                        (ele) => ele?.PhysicalAddressType == PhysicalAddressType.Registered
                                    ) == 0
                                        ? 1
                                        : 0
                                }
                                canChooseCountry={false}
                                onCCMChange={(changes) => {
                                    this.onFullClientChange(changes);
                                }}
                                addressType={PhysicalAddressType.Postal}
                                addressList={data?.AddressDetails}
                                onRef={(ref) => this.appendChild(ref)}
                                onChange={() => {}}
                                data={null}
                                disabled={false}
                            />
                        )}
                    </>
                )}

                <div className="row">
                    <div className="col-fluid col-max-430">
                        <TextBoxEditor
                            value={data.EmailAddressDetails[0]?.EmailAddress}
                            label={"Email address"}
                            inputMode={"email"}
                            regex={{ errorMessage: ValidatorContents.InvalidEmail, value: Email }}
                            disabled={false}
                            onChange={(v) => {
                                this.onFullClientChange([
                                    { value: v, path: "EmailAddressDetails.0.EmailAddress" },
                                    {
                                        value: EmailAddressType.Primary,
                                        path: "EmailAddressDetails.0.EmailAddressType",
                                    },
                                ]);
                            }}
                        />
                    </div>
                </div>

                <PhoneNumberDetails
                    data={data.PhoneNumberDetails}
                    onChange={(value) => {
                        this.onFullClientChange([{ value: value, path: "PhoneNumberDetails" }]);
                    }}
                    disabled={false}
                    onRef={() => {}}
                />

                {!updateClient && (
                    <TaxDetailFatca
                        data={this.props.ClientModel}
                        onChange={(changes) => {
                            this.props.onSetClientModel(changes);
                        }}
                        onCCMChange={(changes) => {
                            this.onClientChange(changes);
                        }}
                        onRef={(ref) => this.appendChild(ref)}
                        disableWhenFullClient={false}
                        existingClientInfo={this.state.existingClientInfo}
                    />
                )}
                <hr className="mb-20" />
            </>
        );
    }
}
